import {
  Button,
  Switch,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from '@you/ui'
import Icon from '@/components/Icon/Icon'
import { KnowledgeDocType, KnowledgeStatus } from '@/api/types/knowledge.types'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useKnowledgeStore } from '@/stores/knowledgeStore'

// 知识库卡片数据接口
interface KnowledgeCardProps {
  id: string
  name: string
  // description: string
  creator: string
  filesNum: number
  docType: KnowledgeDocType
  status: KnowledgeStatus
  onEdit?: (id: string) => void
  onDelete?: (id: string) => void
  onToggleStatus?: (id: string, status: KnowledgeStatus) => void
}

const KnowledgeCard = ({
  id,
  name,
  creator,
  filesNum,
  docType,
  status,
  onEdit,
  onDelete,
  onToggleStatus,
}: KnowledgeCardProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const navigate = useNavigate()
  const { setCurrentKnowledge } = useKnowledgeStore()

  const handleEdit = () => {
    onEdit?.(id)
  }

  const handleDelete = () => {
    setConfirmOpen(true)
  }

  const handleDeleteConfirm = () => {
    setConfirmOpen(false)
    onDelete?.(id)
  }

  const handleDeleteCancel = () => {
    setConfirmOpen(false)
  }

  const handleToggleStatus = (enabled: boolean) => {
    const newStatus = enabled
      ? KnowledgeStatus.ENABLED
      : KnowledgeStatus.DISABLED
    onToggleStatus?.(id, newStatus)
  }

  // 处理卡片点击跳转
  const handleCardClick = () => {
    const knowledgeData = {
      id,
      name,
      docType,
      status,
      creator,
      filesNum,
    }

    setCurrentKnowledge(knowledgeData)
    navigate('/knowledge/dataset')
  }

  // 处理按钮点击，阻止事件冒泡
  const handleButtonClick = (e: React.MouseEvent, callback: () => void) => {
    e.stopPropagation()
    callback()
  }

  // 根据文档类型获取图标名称
  const getIconName = () => {
    return docType === KnowledgeDocType.TEXT ? 'text' : 'table'
  }
  const getDocTypeName = () => {
    return docType === KnowledgeDocType.TEXT ? '文档' : '表格'
  }

  // 获取状态文本
  const getStatusText = () => {
    return status === KnowledgeStatus.ENABLED ? '已启用' : '已停用'
  }

  // 获取状态颜色
  const getStatusColor = () => {
    return status === KnowledgeStatus.ENABLED ? '#461DA2' : '#999999'
  }

  return (
    <div className="w-[300px] h-[158px] rounded-lg border border-[rgba(139,118,243,0.15)] bg-white cursor-pointer hover:shadow-md transition-shadow duration-200">
      <div className="h-full flex flex-col">
        {/* 内容区域 - 可点击跳转 */}
        <div className="flex-1 p-3" onClick={handleCardClick}>
          {/* 标题区域 */}
          <div className="flex items-center space-x-3 mb-3">
            {/* 左侧图标 */}
            <div className="flex-shrink-0">
              <Icon name={getIconName()} size={40} className="text-gray-600" />
            </div>

            {/* 右侧标题和标签 */}
            <div className="flex-1 min-w-0">
              {/* 标题 */}
              <h3 className="text-lg font-semibold text-[#333333] mb-2 line-clamp-1">
                {name}
              </h3>

              {/* 标签 */}
              <div className="flex gap-1 mb-2">
                <span className="inline-block px-2 py-1 text-xs bg-[#E9DFFF4F] text-[#967AD4] rounded">
                  {getDocTypeName()}
                </span>
                <span className="inline-block px-2 py-1 text-xs bg-[#E9DFFF4F] text-[#967AD4] rounded">
                  {filesNum} 个文档
                </span>
                <span className="inline-block px-2 py-1 text-xs bg-[#E8E8E84F] text-[#8C8C8C] rounded flex-none max-w-[100px] truncate">
                  {creator}
                </span>
              </div>
            </div>
          </div>

          {/* 描述 */}
          {/* <p className="text-xs text-[#999999] line-clamp-1">
            {description}
          </p> */}
        </div>

        {/* 底部操作区域 */}
        <div className="h-[40px] border-t border-[#EFF3FD] px-4 flex items-center justify-between flex-none">
          {/* 左侧：编辑和删除按钮 */}
          <div className="flex items-center space-x-4 flex-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => handleButtonClick(e, handleEdit)}
            >
              编辑
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => handleButtonClick(e, handleDelete)}
              className="text-[#C95C2D] hover:text-[#C95C2D] hover:bg-red-50"
            >
              删除
            </Button>
          </div>

          {/* 右侧：启用开关 */}
          <div
            className="flex items-center space-x-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Switch
              checked={status === KnowledgeStatus.ENABLED}
              onCheckedChange={handleToggleStatus}
            />
            <span className="text-sm" style={{ color: getStatusColor() }}>
              {getStatusText()}
            </span>
          </div>
        </div>
        {/* 删除确认弹窗 */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent className="w-[580px]">
            <DialogHeader>
              <div className="flex items-center space-x-2">
                <Icon className="text-[#FF7D00]" name="warning" size={24} />
                <span className="text-[20px]">删除知识库</span>
              </div>
            </DialogHeader>
            <div className="text-center text-[#666666] text-lg py-10">
              知识库删除后无法恢复，请确认是否删除？
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={handleDeleteCancel}>
                取消
              </Button>
              <Button variant="destructive" onClick={handleDeleteConfirm}>
                确定
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}

export default KnowledgeCard
